import { createMuiTheme } from '@material-ui/core/styles'
import { heading } from '../../utils/utils'

const mainFont = 'Raleway'

// Primary Colors
const white = '#fff'
const black = '#000'
const primaryMain = '#E10A0A' // RGB: 225-10-10 rouge
const primaryLight = '#FDF3F3'
const secondaryMain = '#B4D2C1'

// Grey Colors
const greyColors = {
  gray600: '#666666', // RGB: 102-102-102
  gray400: '#b0b0b0', // RGB: 128-128-128
  gray500: '#898888', // RGB: 137-136-136
  gray300: '#999999', // RGB: 153-153-153
  greyLight: '#CCCCCC', // RGB: 204-204-204
  greyMedium: '#D9D9D9', // RGB: 217-217-217
  greyText: '#F2F2F2', // RGB: 242-242-242
}

// Alert Colors
const alertColors = {
  error: '#E10A0A', // Same as primaryMain
  warning: '#F2CE00', // RGB: 242-206-0
  success: '#00A652', // RGB: 0-166-82
}

// Font sizes
const f12 = '12px'
const f13 = '13px'
const f14 = '14px'
const f15 = '15px'
const f16 = '16px'
const f18 = '18px'
const f24 = '24px'
const f32 = '32px'

// Text styles
const body1 = {
  fontSize: f14,
  lineHeight: 1.6,
}

const body2 = {
  fontSize: f13,
  lineHeight: 1.67,
}

const subtitle1 = {
  fontSize: f12,
  lineHeight: 1.25,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
}

const subtitle2 = {
  fontSize: f13,
  lineHeight: 1.25,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
}

// Add this after color definitions
const breakpoints = {
  xs: 0,
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1440,
} as const

const theme = createMuiTheme({
  themeFont: {
    main: 'Raleway',
  },
  breakpoints: {
    values: breakpoints,
  },
  typography: {
    fontFamily: `${mainFont}, sans-serif`,
    h1: {
      lineHeight: 1.15,
      ...heading('18px', '35px', 700, 700, mainFont, mainFont, false),
    },
    h2: {
      lineHeight: 1.15,
      ...heading('16px', '30px', 600, 700, mainFont, mainFont, false),
    },
    h3: {
      lineHeight: 1.4,
      ...heading(f18, '28px', 700, 700, mainFont, mainFont, false),
    },
    h4: {
      lineHeight: 1.55,
      ...heading(f16, '21px', 700, 700, mainFont, mainFont, false),
    },
    h5: {
      lineHeight: 1.25,
      ...heading(f14, '20px', 600, 700, mainFont, mainFont, false),
    },
    h6: {
      lineHeight: 1.25,
      ...heading(f12, f12, 600, 700, mainFont, mainFont, false),
    },
    subtitle1: {
      ...subtitle1,
      [`@media (min-width: ${breakpoints.md}px)`]: {
        fontSize: '14px',
      },
    },
    subtitle2: {
      ...subtitle2,
      [`@media (min-width: ${breakpoints.md}px)`]: {
        fontSize: '13px',
      },
    },
    body1: {
      ...body1,
      fontWeight: 400,
      fontFamily: mainFont,
      [`@media (min-width: ${breakpoints.md}px)`]: {
        fontSize: '16px',
      },
      color: black,
    },
    body2: {
      ...body2,
      fontWeight: 400,
      fontFamily: mainFont,
      [`@media (min-width: ${breakpoints.md}px)`]: {
        fontSize: '15px',
      },
      color: black,
    },
    caption: {
      fontSize: f12,
      lineHeight: 1.17,
      fontWeight: 400,
      fontFamily: mainFont,
    },
  },
  palette: {
    common: {
      black,
      white,
    },
    background: {
      default: white,
      paper: white,
    },
    primary: {
      main: primaryMain,
      light: primaryLight,
    },
    secondary: {
      main: secondaryMain,
    },
    error: {
      main: alertColors.error,
    },
    warning: {
      main: alertColors.warning,
    },
    success: {
      main: alertColors.success,
    },
    divider: greyColors.greyText,
    primaryTransparent: {
      main: primaryMain,
      light: primaryLight,
    },
    secondaryTransparent: {
      main: primaryMain,
      light: primaryLight,
    },
    ternary: {
      main: '#0082C3',
      light: '#F1FAFF',
    },
    accent: {
      main: '#FFEA28',
    },
  },
  sizes: {
    headerHeight: 60,
    headerHeightTransparent: 90,
    logoWidth: 110,
    logoHeight: 30,
    footerBG: 168,
    basketWidth: 340,
    basketWidthLG: 440,
  },
  extraColors: {
    grey: greyColors.gray600,
    greyLight: greyColors.greyText,
    greyMedium: greyColors.gray300,
    greyText: greyColors.gray500,
  },
  shadows: [
    'none',
    '0px 0px 15px rgba(46, 46, 46, 0.1)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
  spacing: 8,
  shape: {
    borderRadius: 0,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '.title': {
          fontSize: '18px !important',
          '@media (min-width: 1024px)': {
            fontSize: '35px !important',
          },
        },
        '.header': {
          backgroundColor:
            'white !important' /*backgroundColor: `${white} !important`,*/,

          '&__left,&__right': {
            '@media (min-width: 1024px)': {
              width: '20% !important',
            },
          },
          '&__center': {
            '@media (min-width: 1024px)': {
              width: '60% !important',
            },
          },
          '&__cta': {
            height: 'auto !important',
            width: 'fit-content !important',
          },
        },
        '.section-discover': {
          padding: '2rem 0 3rem 0 !important',
          '&__subtitle': {
            textAlign: 'center',
          },
          '& .discover__shadow': {
            display: 'flex !important',
            flexDirection: 'column-reverse !important',
          },
        },

        '.footer': {
          '&__bottomRow2': {
            '@media (min-width: 1024px)': {
              flexDirection: 'column-reverse',
              alignItems: 'flex-start !important',
            },
          },
          '&__menu': {
            alignSelf: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            '&>a': {
              margin: '0 !important',
            },
          },
        },
        '.daterange': {
          color: `${black} !important`,
          '&__btn': {
            color: `${black} !important`,
          },
          '&__icon--arrow': {
            color: `${black} !important`,
          },
        },
        '.search_input_container': {
          backgroundColor: `${primaryLight} !important`,
          border: 'none !important',
        },
        '.section-faq': {
          backgroundColor: `${primaryLight} !important`,
        },
        '.productCard': {
          '&__cta': {
            width: 'fit-content',
            backgroundColor: `${primaryMain} !important`,
            '&:hover': {
              backgroundColor: `${primaryMain} !important`,
            },
            '& svg': {
              color: `${white} !important`,
            },
          },
          '&__desc': {
            '& .price__amount, & .price__duration': {
              color: `${black} !important`,
            },
          },
          '&__title': {
            fontWeight: '700 !important',
          },
          '&__price--original': {
            fontWeight: '400 !important',
            '& .price__amount': {
              fontWeight: '700 !important',
            },
          },
        },
        '.hiw': {
          '&__icon': {
            width: 'fit-content',
            margin: 'auto',
            backgroundColor: `${primaryLight}`,
            borderRadius: '50%',
          },
          '&__img': {
            width: '80px !important',
            height: '80px !important',
            padding: '20px',
          },
        },
        '.section-hiw': {
          padding: '3rem 0 1rem 0 !important',
          '@media (max-width: 1024px)': {
            padding: '1rem 0 !important',
          },
          '&__title': {
            color: `${black} !important`,
          },
          '&__cta': {
            backgroundColor: ` ${white} !important`,
            color: ` ${black} !important`,
            border: `1px solid ${black}`,
            '&:hover': {
              backgroundColor: ` ${white} !important`,
            },
          },
          '&__spacing': {
            padding: '4rem 0 1rem 0 !important',
            '@media (max-width: 1024px)': {
              padding: '0 !important',
            },
          },
        },
        '.react-datepicker__navigation,.react-datepicker__day--in-range,.react-datepicker__day--today':
          {
            backgroundColor: `${primaryMain} !important`,
          },
        '.cart': {
          backgroundColor: `${primaryLight} !important`,
          '&-badge': {
            backgroundColor: ` ${primaryMain} !important`,
          },
          '& .shipping-infos': {
            '& p': {
              fontWeight: '400 !important',
            },
            '& span': {
              fontWeight: '700 !important',
            },
          },
          '& .price__amount,& .price__duration': {
            color: ` ${black} !important`,
          },
          '& .quantitySelector__btn, & .quantitySelector__total': {
            borderRadius: '9999px !important',
          },
          '& .quantitySelector__total': {
            width: '32px !important',
            height: '32px !important',
            backgroundColor: `${black} !important`,
            color: `${white} !important`,
          },
          '& .quantitySelector__btn': {
            backgroundColor: `${greyColors.greyText} !important`,
            borderColor: `${greyColors.greyText} !important`,
            color: `${black} !important`,
          },
        },
        '.shipping-infos__wrapper': {
          '& .price__amount,& .price__duration': {
            color: ` ${black} !important`,
          },
        },
        '.funnel': {
          '&__nav': {
            '&--active': {
              color: ` ${primaryMain} !important`,
              borderColor: ` ${primaryMain} !important`,
            },
          },
        },
        '.price': {
          '&__amount,&__duration': {
            color: ` ${primaryMain} !important`,
          },
        },
        '.deliveryDate,.returnDate': {
          color: ` ${primaryMain} !important`,
        },
        '.discover__shadow': {
          boxShadow: 'none!important',
        },
        '.discover__media': {},
        '.discover-product__content': {
          backgroundColor: 'transparent !important',
        },
        '.onlyAvailable': {
          '&__check': {
            color: `${black} !important`,
          },
        },
        '.discover-product': {
          '&__content': {
            backgroundColor: 'transparent !important',
          },
          '&__title': {
            textAlign: 'center',
          },
        },
        '.cart-badge': {
          color: `${white} !important`,
          background: `${primaryMain} !important`,
        },
        '.recapOrder': {
          '&__title': {
            '& h3': {
              color: `${primaryMain} !important`,
            },
          },
          '&__account_cta': {
            color: `${primaryMain} !important`,
            borderColor: ` ${primaryMain} !important`,
          },
        },
        '.menu': {
          '&__item,&__link': {
            fontWeight: '700 !important',
          },
          '&-mobile__item,&-mobile__item a': {
            fontWeight: '700 !important',
          },
        },
        '.shop-menu__sort-input': {
          borderRadius: '0px !important',
        },
        '.shop__planner': {
          '& .shipping-infos': {
            display: 'flex !important',
            alignItems: 'center !important',
            '&__start,&__end': {
              padding: '5px 16px !important',
            },
          },
        },
        '.product__breadcrumbs': {
          '& li,& a': {
            color: `${black} !important`,
          },
          '& ol>li:last-of-type a': {
            color: `${primaryMain} !important`,
          },
        },
        '.faq-page': {
          '&__anchor': {
            height: '30px !important',
            width: 'fit-content !important',
          },
        },
        '.commitment__title': {
          fontSize: '18px !important',
        },
        '.basket__popup__title+button': {
          width: 'fit-content !important',
        },
      },
    },
    MuiButton: {
      root: {
        width: '260px',
        height: '32px',
        fontSize: '14px',
        textTransform: 'uppercase' as const,
        fontFamily: 'Raleway',
        borderRadius: 0,
        fontWeight: 700,
        [`@media (min-width: ${breakpoints.md}px)`]: {
          width: '256px',
          height: '44px',
        },
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
          /*           opacity: 0.75,
           */
        },
        '&:disabled': {
          opacity: 0.52,
        },
        '&:active': {
          opacity: 0.85,
        },
      },
      containedPrimary: {
        backgroundColor: primaryMain,
        color: white,
        '&:hover': {
          backgroundColor: `${primaryMain} !important`,
        },
      },
      containedSecondary: {
        backgroundColor: primaryMain,
        color: white,
        /* border: `1px solid ${black}`, */
        '&:hover': {
          backgroundColor: `${primaryMain} !important`,
        },
      },
      outlinedPrimary: {
        '&:hover': {
          backgroundColor: `${white} !important`,
        },
      },
    },
    MuiPaper: {
      root: {
        boxShadow: '0px 0px 15px rgba(46, 46, 46, 0.1)',
      },
      rounded: {
        borderRadius: 0,
      },
    },
    MuiSelect: {
      root: {
        color: primaryMain,
      },
      selectMenu: {
        '&:focus': {
          background: white,
        },
      },
    },
    MuiChip: {
      root: {
        borderRadius: 0,
        fontSize: 15,
        fontWeight: 700,
        color: black,
        backgroundColor: 'transparent',
        height: 25,
        padding: 0,
      },
      label: {
        padding: 0,
      },
      sizeSmall: {
        height: 22,
        fontSize: 12,
        fontWeight: 400,
      },
      labelSmall: {
        paddingLeft: 10,
        paddingRight: 10,
      },
      colorPrimary: {
        color: primaryMain,
        backgroundColor: 'transparent',
      },
      colorSecondary: {
        color: secondaryMain,
        backgroundColor: 'transparent',
      },
    },
  },
})

export default theme
